import {
  Box,
  Button,
  Center,
  CopyButton,
  Flex,
  Grid,
  Modal,
  PinInput,
  Text,
} from "@mantine/core";
import { QRCodeSVG } from "qrcode.react";
import { LuInfo } from "react-icons/lu";
import { useEffect, useState } from "react";
import swal from "sweetalert";
import useStyles from "./style";
import {
  disableTotp,
  setupTotp,
  verifyTotpSetup,
} from "../../services";
import success2FAIcon from "../../images/hugeiconsnoteremove.png";

const SetupAuthenticatorModal = ({ opened, close, profile, mode, onSuccess }) => {
  const { classes } = useStyles();
  const userId = profile?.user?.id;
  const isDisable = mode === "disable";

  const [loadingSecret, setLoadingSecret] = useState(false);
  const [secret, setSecret] = useState("");
  const [otpauthUrl, setOtpauthUrl] = useState("");
  const [otp, setOtp] = useState("");
  const [error, setError] = useState("");
  const [verifying, setVerifying] = useState(false);

  useEffect(() => {
    if (!opened) return;
    setOtp("");
    setError("");

    if (isDisable) return;

    const loadSecret = async () => {
      setLoadingSecret(true);
      const res = await setupTotp({ userId });
      if (res?.secret) {
        setSecret(res.secret);
        setOtpauthUrl(res.otpauthUrl);
      }
      setLoadingSecret(false);
    };
    loadSecret();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, isDisable]);

  const successSetup = async () => {
    await swal({
      title: "Success!",
      text: "Your authenticator app is now enabled. You'll be asked for a code from your app when 2FA is required.",
      icon: success2FAIcon,
      buttons: {
        confirm: {
          text: "OK",
          visible: true,
          className: classes.customokbutton,
        },
      },
      dangerMode: false,
    });
  };

  const successDisable = async () => {
    await swal({
      title: "Success!",
      text: "Your authenticator app has been disabled.",
      icon: success2FAIcon,
      buttons: {
        confirm: {
          text: "OK",
          visible: true,
          className: classes.customokbutton,
        },
      },
      dangerMode: false,
    });
  };

  const handleVerify = async () => {
    try {
      setError("");
      if (otp?.length < 6) {
        setError("Please enter the complete 6-digit code.");
        return;
      }
      setVerifying(true);

      if (isDisable) {
        const res = await disableTotp({ userId, code: otp });
        if (res) {
          setOtp("");
          onSuccess?.(false);
          close();
          successDisable();
        } else {
          setError("Incorrect code. Please try again.");
        }
      } else {
        const res = await verifyTotpSetup({ userId, code: otp });
        if (res) {
          setOtp("");
          onSuccess?.(true);
          close();
          successSetup();
        } else {
          setError("Incorrect code. Please try again.");
        }
      }
    } finally {
      setVerifying(false);
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={() => {
        setOtp("");
        setError("");
        close();
      }}
      centered
      size={690}
      shadow="md"
      radius={"lg"}
      zIndex={1000}
      overlayProps={{ blur: 3 }}
      classNames={{ header: classes.modalHeader }}
      className={classes.modalBody}
      title={
        <Text className={classes.modalTitle}>
          {isDisable ? "Disable authenticator app" : "Set up authenticator app"}
          <LuInfo />
        </Text>
      }
    >
      <Grid className={classes.contentBoxParent}>
        <Grid.Col xs={12} sm={12} md={12} className={classes.contentBox}>
          <Box w="100%">
            {isDisable ? (
              <Text className="infoText" mb="md">
                Enter the current code from your authenticator app to confirm
                disabling it.
              </Text>
            ) : (
              <>
                <Text className="infoText" mb="md">
                  Scan this QR code with Google Authenticator, Microsoft Authenticator, or
                  any TOTP authenticator app.
                </Text>
                <Center mb="md">
                  {loadingSecret ? (
                    <Text fz={14} color="#737373">
                      Generating your code...
                    </Text>
                  ) : otpauthUrl ? (
                    <Box className={classes.qrBox}>
                      <QRCodeSVG value={otpauthUrl} size={180} />
                    </Box>
                  ) : (
                    <Text fz={14} color="red">
                      Couldn't generate a code. Please close and try again.
                    </Text>
                  )}
                </Center>
                {secret && (
                  <Flex direction="column" align="center" gap={6} mb="md">
                    <Text fz={12} color="#737373">
                      Can't scan? Enter this key manually:
                    </Text>
                    <Flex align="center" gap={8}>
                      <Text className={classes.secretKeyBox}>{secret}</Text>
                      <CopyButton value={secret} timeout={1500}>
                        {({ copied, copy }) => (
                          <Button size="xs" variant="default" onClick={copy}>
                            {copied ? "Copied" : "Copy"}
                          </Button>
                        )}
                      </CopyButton>
                    </Flex>
                  </Flex>
                )}
                <Text className="infoText" fw={600} mb="sm">
                  Then enter the 6-digit code it shows:
                </Text>
              </>
            )}

            <form
              onSubmit={(e) => {
                e.preventDefault();
                handleVerify();
              }}
            >
              <Center>
                <PinInput
                  length={6}
                  size="xl"
                  type="number"
                  oneTimeCode
                  value={otp}
                  onChange={setOtp}
                  placeholder="0"
                  classNames={{ input: classes.pinInput }}
                />
              </Center>

              {error && (
                <Text color="red" ta="center" ff="Inter" mt={10}>
                  {error}
                </Text>
              )}

              <Center>
                <Button
                  size="md"
                  radius="md"
                  className={classes.login_btn}
                  loading={verifying}
                  type="submit"
                  disabled={!isDisable && !otpauthUrl}
                >
                  {verifying
                    ? "Verifying..."
                    : isDisable
                    ? "Disable authenticator app"
                    : "Verify & enable"}
                </Button>
              </Center>
            </form>
          </Box>
        </Grid.Col>
      </Grid>
    </Modal>
  );
};

export default SetupAuthenticatorModal;
